import { tweenNumber } from '../lib/anim.js';
import {
  ApiError,
  type LastSet,
  type SessionSetRow,
  type WorkoutDetail,
  type WorkoutExercise,
  api,
} from '../lib/api.js';
import { el, mount, toast, uuid } from '../lib/dom.js';
import { confirmDialog, promptDialog } from '../lib/modal.js';
import { enqueueSet, flush } from '../lib/outbox.js';
import { type PlateResult, calculatePlates, summarize } from '../lib/plates.js';
import { navigate } from '../lib/router.js';
import { isMuted, playClick, toggleMuted } from '../lib/sound.js';
import { type TimerHandle, startRestTimer } from '../lib/timer.js';
import * as wakeLock from '../lib/wakeLock.js';

type LoggedSet = {
  client_uuid: string;
  exercise_id: number;
  set_number: number;
  weight_kg: number;
  reps: number;
};

type SessionState = {
  sessionId: number;
  workoutCode: string;
  exercises: WorkoutExercise[];
  currentIdx: number;
  logged: LoggedSet[]; // local mirror across all exercises in this session
};

let activeTimer: TimerHandle | null = null;

// Sticky timer DOM, shared across re-renders of the session view.
let timerBox: HTMLElement | null = null;
let timerTimeNode: HTMLElement | null = null;

// True while a session view is mounted. Used to release the wake lock and stop
// the rest timer when the user leaves the session by any route change (browser
// back, nav tap), not just via the in-view "Sair" button.
let sessionActive = false;

function teardownSession() {
  if (!sessionActive) return;
  sessionActive = false;
  activeTimer?.stop();
  activeTimer = null;
  if (timerBox) timerBox.hidden = true;
  void wakeLock.release();
}

// Registered once: any navigation away from /session/* tears down side effects.
window.addEventListener('hashchange', () => {
  if (!location.hash.startsWith('#/session/')) teardownSession();
});

function ensureTimerNodes(): { box: HTMLElement; time: HTMLElement } {
  if (timerBox && timerTimeNode) return { box: timerBox, time: timerTimeNode };
  const time = el('div', { class: 'timer__time' }, ['00:00']);
  const box = el('div', { class: 'timer', hidden: true } as never, [
    el('div', { class: 'timer__label' }, ['Descanso']),
    time,
    el(
      'button',
      {
        class: 'btn btn--ghost',
        type: 'button',
        style: 'min-height: var(--tap-min); padding: 0 var(--space-3); font-size: var(--text-sm)',
        onclick: () => {
          activeTimer?.stop();
          if (timerBox) timerBox.hidden = true;
        },
      },
      ['Pular'],
    ),
  ]);
  timerBox = box;
  timerTimeNode = time;
  return { box, time };
}

function startRest(seconds: number) {
  const { box, time } = ensureTimerNodes();
  activeTimer?.stop();
  box.hidden = false;
  activeTimer = startRestTimer(seconds, time, (overdueSeconds) => {
    if (overdueSeconds > 3) {
      toast(`Descanso acabou há ${overdueSeconds}s`);
    }
    // Stay visible at 00:00 for ~2.5s, then auto-hide.
    setTimeout(() => {
      if (timerBox) timerBox.hidden = true;
    }, 2500);
  });
}

function stopRest() {
  activeTimer?.stop();
  activeTimer = null;
  if (timerBox) timerBox.hidden = true;
}

export async function renderSession(params: Record<string, string>) {
  const sessionId = Number(params.id);
  if (!Number.isInteger(sessionId) || sessionId <= 0) {
    toast('Sessão inválida');
    navigate('/');
    return;
  }
  const urlCode = new URLSearchParams(location.hash.split('?')[1] ?? '').get('code') ?? 'A';
  // Load the workout and any sets already logged in this session in parallel,
  // so resuming/reloading shows real progress instead of restarting at set 1.
  const [{ workout, exercises }, logged] = await Promise.all([
    api.get<WorkoutDetail>(`/workouts/${urlCode}`),
    api
      .get<{ sets: SessionSetRow[] }>(`/sessions/${sessionId}/sets`)
      .then((r) => r.sets)
      .catch(() => [] as SessionSetRow[]),
  ]);

  // Stop any timer left from a previous session before resetting its DOM.
  activeTimer?.stop();
  activeTimer = null;
  sessionActive = true;
  void wakeLock.acquire();
  // Reset timer state for a fresh session.
  timerBox = null;
  timerTimeNode = null;

  // Start on the first exercise that still has sets to log.
  const firstUnfinished = exercises.findIndex(
    (ex) => logged.filter((s) => s.exercise_id === ex.id).length < ex.default_sets,
  );

  const state: SessionState = {
    sessionId,
    workoutCode: workout.code,
    exercises,
    currentIdx: firstUnfinished === -1 ? 0 : firstUnfinished,
    // A set needs its client_uuid to be editable/deletable; the UI always
    // sends one, so this filter is just defensive against legacy rows.
    logged: logged
      .filter((s) => typeof s.client_uuid === 'string' && s.client_uuid.length > 0)
      .map((s) => ({
        client_uuid: s.client_uuid,
        exercise_id: s.exercise_id,
        set_number: s.set_number,
        weight_kg: s.weight_kg,
        reps: s.reps,
      })),
  };

  // Resuming a session that's already fully logged: go straight to the finish
  // card (where it gets finalized) instead of a dead, all-done exercise card.
  if (firstUnfinished === -1 && state.logged.length > 0) {
    renderFinishCard(state);
    return;
  }

  paint(state);
}

function paint(state: SessionState) {
  const ex = state.exercises[state.currentIdx];
  if (!ex) {
    renderFinishCard(state);
    return;
  }

  const setsForEx = state.logged.filter((s) => s.exercise_id === ex.id);
  const totalSets = ex.default_sets;
  const loggedNums = new Set(setsForEx.map((s) => s.set_number));
  // Next slot = first 1..totalSets not yet logged. Using the first GAP (not the
  // count) means deleting a middle set re-opens its slot instead of duplicating
  // the last one.
  let nextSetNumber = totalSets + 1;
  for (let i = 1; i <= totalSets; i++) {
    if (!loggedNums.has(i)) {
      nextSetNumber = i;
      break;
    }
  }
  const completed = Math.min(setsForEx.length, totalSets);
  const allDone = nextSetNumber > totalSets;

  // Reference: last session and target (DDP suggestion).
  const refs = el('div', { class: 'exercise-card__reference' }, [
    el('div', { class: 'ref' }, [
      el('div', { class: 'ref__label' }, ['Última']),
      el('div', { class: 'ref__value' }, [formatLastSet(ex.last_sets)]),
    ]),
    el('div', { class: 'ref ref--target' }, [
      el('div', { class: 'ref__label' }, ['Alvo']),
      el('div', { class: 'ref__value' }, [formatTarget(ex.suggestion)]),
    ]),
  ]);

  // Rows: previous logged sets + current row (active) + remaining placeholders.
  const rows: HTMLElement[] = [];
  for (let i = 1; i <= totalSets; i++) {
    if (loggedNums.has(i)) {
      const s = setsForEx.find((x) => x.set_number === i);
      rows.push(loggedRow(i, s, (set) => void editLoggedSet(state, set, ex)));
    } else if (i === nextSetNumber && !allDone) {
      rows.push(currentRow(i));
    } else {
      rows.push(pendingRow(i));
    }
  }

  // Starting values for the current set.
  // Priority: previous set in THIS session (carry forward) → last set from
  // previous session (same set_number, then any) → DDP target → 0.
  const prevInSession = setsForEx.filter((s) => s.set_number === nextSetNumber - 1).at(-1);
  const lastFromServer =
    ex.last_sets?.find((s) => s.set_number === nextSetNumber) ?? ex.last_sets?.at(-1);

  const startingWeight =
    prevInSession?.weight_kg ??
    lastFromServer?.weight_kg ??
    (ex.suggestion.reason !== 'first-time' ? ex.suggestion.weight_kg : null) ??
    0;
  const startingReps = prevInSession?.reps ?? lastFromServer?.reps ?? ex.suggestion.reps;

  const weightInput = el('input', {
    class: 'input input--num',
    type: 'number',
    inputMode: 'decimal',
    step: '0.5',
    min: '0',
    value: String(startingWeight),
    'aria-label': 'Peso em kg',
  });
  const repsInput = el('input', {
    class: 'input input--num',
    type: 'number',
    inputMode: 'numeric',
    step: '1',
    min: '0',
    value: String(startingReps),
    'aria-label': 'Repetições',
  });

  const stepper = (input: HTMLInputElement, step: number) =>
    el('div', { class: 'stepper' }, [
      el(
        'button',
        {
          class: 'stepper__btn',
          type: 'button',
          onclick: () => {
            tweenNumber(input, Math.max(0, Number(input.value || 0) - step));
          },
        },
        ['−'],
      ),
      input,
      el(
        'button',
        {
          class: 'stepper__btn',
          type: 'button',
          onclick: () => {
            tweenNumber(input, Number(input.value || 0) + step);
          },
        },
        ['+'],
      ),
    ]);

  const doneBtn = el(
    'button',
    {
      class: 'btn btn--primary btn--cta',
      type: 'button',
      disabled: allDone,
      onclick: async () => {
        if (allDone) return;
        const weight = Number(weightInput.value);
        const reps = Number(repsInput.value);
        if (!Number.isFinite(weight) || weight < 0 || !Number.isFinite(reps) || reps <= 0) {
          toast('Preenche peso e reps');
          return;
        }
        const setNumber = nextSetNumber;

        playClick();
        // Optimistic update + enqueue.
        const cuid = uuid();
        state.logged.push({
          client_uuid: cuid,
          exercise_id: ex.id,
          set_number: setNumber,
          weight_kg: weight,
          reps,
        });
        await enqueueSet({
          client_uuid: cuid,
          session_id: state.sessionId,
          exercise_id: ex.id,
          set_number: setNumber,
          weight_kg: weight,
          reps,
          rpe: null,
        });

        startRest(ex.rest_seconds);

        // Advance to the next exercise once every slot of this one is filled
        // (counting the set we just pushed). Handles filling a re-opened gap.
        const nowComplete = state.logged.filter((s) => s.exercise_id === ex.id).length >= totalSets;
        if (nowComplete) {
          const nextIdx = state.currentIdx + 1;
          state.currentIdx = nextIdx;
          if (nextIdx >= state.exercises.length) {
            stopRest();
            renderFinishCard(state);
            return;
          }
          setTimeout(() => paint(state), 250);
        } else {
          paint(state);
        }
      },
    },
    [allDone ? `✓ Exercício concluído` : `✓ Série ${nextSetNumber} feita`],
  );

  const cardChildren: (Node | null)[] = [
    el('div', { class: 'exercise-card__title' }, [
      el('div', { class: 'exercise-card__name' }, [ex.name]),
      el('div', { class: 'exercise-card__pos' }, [
        allDone ? `${totalSets}/${totalSets} ✓` : `Série ${nextSetNumber}/${totalSets}`,
      ]),
    ]),
    refs,
    el(
      'ul',
      { class: 'stack stack--sm' },
      rows.map((r) => el('li', {}, [r])),
    ),
    el('div', { class: 'inputs' }, [
      el('div', { class: 'field' }, [
        el('div', { class: 'field__label' }, ['Peso (kg)']),
        stepper(weightInput as HTMLInputElement, 1),
      ]),
      el('div', { class: 'field' }, [
        el('div', { class: 'field__label' }, ['Reps']),
        stepper(repsInput as HTMLInputElement, 1),
      ]),
    ]),
    ex.uses_barbell ? plateWidget(ex.bar_weight_kg, weightInput as HTMLInputElement) : null,
    doneBtn,
  ];
  const card = el('section', { class: 'exercise-card' }, cardChildren.filter(Boolean) as Node[]);

  const pager = el('div', { class: 'exercise-pager' }, [
    el(
      'button',
      {
        class: 'btn btn--ghost',
        type: 'button',
        disabled: state.currentIdx === 0,
        onclick: () => {
          state.currentIdx = Math.max(0, state.currentIdx - 1);
          paint(state);
        },
      },
      ['‹ Anterior'],
    ),
    el(
      'div',
      { class: 'exercise-pager__dots' },
      state.exercises.map((_, i) => {
        const cls =
          i === state.currentIdx
            ? 'exercise-pager__dot exercise-pager__dot--active'
            : i < state.currentIdx
              ? 'exercise-pager__dot exercise-pager__dot--done'
              : 'exercise-pager__dot';
        return el('span', { class: cls });
      }),
    ),
    el(
      'button',
      {
        class: 'btn btn--ghost',
        type: 'button',
        onclick: () => {
          state.currentIdx = Math.min(state.exercises.length, state.currentIdx + 1);
          paint(state);
        },
      },
      ['Próximo ›'],
    ),
  ]);

  mount(
    el('main', { class: 'view' }, [
      el('header', { class: 'session-head' }, [
        el('div', {}, [
          el('div', { class: 'field__label' }, [`Treino ${state.workoutCode}`]),
          el('div', { style: 'font-size: var(--text-lg); font-weight: 600' }, [
            `Exercício ${state.currentIdx + 1} de ${state.exercises.length}`,
          ]),
        ]),
        el('div', { class: 'row', style: 'gap: var(--space-2)' }, [
          muteButton(),
          el(
            'button',
            {
              class: 'btn btn--ghost',
              type: 'button',
              onclick: async () => {
                const ok = await confirmDialog({
                  title: 'Sair sem finalizar?',
                  message: 'O treino fica em aberto. Você pode voltar depois pela tela inicial.',
                  okLabel: 'Sair',
                  danger: true,
                });
                if (!ok) return;
                activeTimer?.stop();
                void wakeLock.release();
                navigate('/');
              },
            },
            ['Sair'],
          ),
        ]),
      ]),
      ensureTimerNodes().box,
      card,
      pager,
      el(
        'button',
        {
          class: 'btn btn--cta',
          type: 'button',
          style: 'margin-top: var(--space-3)',
          onclick: () => renderFinishCard(state),
        },
        [
          (() => {
            const p = computeProgress(state);
            const done = p.filter((x) => x.done).length;
            return done === p.length ? 'Finalizar treino' : `Resumo (${done}/${p.length} prontos)`;
          })(),
        ],
      ),
    ]),
  );
}

function plateWidget(barKg: number, weightInput: HTMLInputElement): HTMLElement {
  const visual = el('div', { class: 'plates__visual' });
  const summary = el('div', { class: 'plates__summary' });
  const remainder = el('span', { class: 'plates__remainder' });
  const head = el('div', { class: 'plates__head' }, [
    el('span', {}, [`Por lado · barra ${barKg}kg`]),
    remainder,
  ]);
  const wrap = el('div', { class: 'plates' }, [head, visual, summary]);

  const paintWidget = () => {
    const target = Number(weightInput.value || 0);
    const result = calculatePlates(target, barKg);
    if (!result) {
      // Hide widget entirely when target is below the bar weight.
      wrap.hidden = true;
      return;
    }
    wrap.hidden = false;
    summary.textContent = `${result.side_kg}kg por lado · ${summarize(result)}`;
    remainder.textContent = result.remainder_kg > 0 ? `falta ${result.remainder_kg}kg` : '';
    visual.replaceChildren(...renderPlateStack(result));
  };

  weightInput.addEventListener('input', paintWidget);
  paintWidget();
  return wrap;
}

function renderPlateStack(result: PlateResult): Node[] {
  // Render left side (heaviest near bar), the bar, then right side mirrored.
  const left: HTMLElement[] = [];
  const right: HTMLElement[] = [];
  for (const p of result.plates) {
    const cls = plateClass(p.kg);
    for (let i = 0; i < p.count; i++) {
      left.push(
        el('span', { class: `plate ${cls}`, title: `${p.kg}kg`, 'aria-label': `${p.kg}kg` }, [
          labelOf(p.kg),
        ]),
      );
      right.push(
        el('span', { class: `plate ${cls}`, title: `${p.kg}kg`, 'aria-label': `${p.kg}kg` }, [
          labelOf(p.kg),
        ]),
      );
    }
  }
  // Heaviest closest to bar → reverse the left side.
  left.reverse();
  return [...left, el('span', { class: 'plate-bar' }), ...right];
}

function plateClass(kg: number): string {
  if (kg >= 20) return 'plate--20';
  if (kg >= 15) return 'plate--15';
  if (kg >= 10) return 'plate--10';
  if (kg >= 5) return 'plate--5';
  if (kg >= 2.5) return 'plate--2';
  return 'plate--1';
}

function labelOf(_kg: number): string {
  return '';
}

function muteButton(): HTMLElement {
  const btn = el(
    'button',
    {
      class: 'btn btn--ghost',
      type: 'button',
      'aria-label': isMuted() ? 'Ativar som' : 'Mutar',
      onclick: () => {
        const muted = toggleMuted();
        btn.textContent = muted ? '🔇' : '🔊';
        btn.setAttribute('aria-label', muted ? 'Ativar som' : 'Mutar');
      },
    },
    [isMuted() ? '🔇' : '🔊'],
  );
  return btn;
}

function loggedRow(i: number, s: LoggedSet | undefined, onEdit?: (set: LoggedSet) => void) {
  const row = el(
    'div',
    {
      class: 'set-row set-row--logged',
      style: s && onEdit ? 'cursor: pointer' : '',
      onclick: s && onEdit ? () => onEdit(s) : undefined,
    },
    [
      el('span', { class: 'set-row__num' }, [`${i}`]),
      el('span', { class: 'set-row__cell' }, [s ? `${s.weight_kg}kg` : '—']),
      el('span', { class: 'set-row__cell' }, [s ? `${s.reps} reps` : '—']),
      el('span', { class: 'set-row__check' }, ['✓']),
    ],
  );
  return row;
}

function pendingRow(i: number) {
  return el('div', { class: 'set-row' }, [
    el('span', { class: 'set-row__num' }, [`${i}`]),
    el('span', { class: 'set-row__cell dim' }, ['—']),
    el('span', { class: 'set-row__cell dim' }, ['—']),
    el('span', {}),
  ]);
}

async function editLoggedSet(state: SessionState, set: LoggedSet, ex: WorkoutExercise) {
  const result = await promptDialog<{ weight: string; reps: string }>({
    title: `Série ${set.set_number} · ${ex.name}`,
    fields: [
      {
        key: 'weight',
        label: 'Peso (kg)',
        value: String(set.weight_kg),
        inputMode: 'decimal',
        step: String(ex.weight_step_kg),
        min: '0',
      },
      {
        key: 'reps',
        label: 'Repetições',
        value: String(set.reps),
        inputMode: 'numeric',
        step: '1',
        min: '0',
      },
    ],
    okLabel: 'Salvar',
    destructiveLabel: 'Apagar série',
  });

  if (result.destructive) {
    const ok = await confirmDialog({
      title: `Apagar série ${set.set_number}?`,
      message: 'Essa série será removida do treino.',
      okLabel: 'Apagar',
      danger: true,
    });
    if (!ok) return;
    await deleteLoggedSet(state, set);
    paint(state);
    return;
  }
  if (!result.ok) return;

  const newWeight = Number(result.values.weight.replace(',', '.'));
  if (!Number.isFinite(newWeight) || newWeight < 0) {
    toast('Peso inválido');
    return;
  }
  const newReps = Number(result.values.reps);
  if (!Number.isFinite(newReps) || newReps <= 0 || !Number.isInteger(newReps)) {
    toast('Reps inválido');
    return;
  }

  const idx = state.logged.findIndex((s) => s.client_uuid === set.client_uuid);
  if (idx === -1) return;
  state.logged[idx] = { ...set, weight_kg: newWeight, reps: newReps };

  await enqueueSet({
    client_uuid: set.client_uuid,
    session_id: state.sessionId,
    exercise_id: set.exercise_id,
    set_number: set.set_number,
    weight_kg: newWeight,
    reps: newReps,
    rpe: null,
  });

  paint(state);
}

async function deleteLoggedSet(state: SessionState, set: LoggedSet) {
  state.logged = state.logged.filter((s) => s.client_uuid !== set.client_uuid);
  // Best-effort delete on server. If offline, this just fails silently —
  // the row is gone from the UI; next reload will re-sync from server.
  try {
    await api.del(`/sets/${encodeURIComponent(set.client_uuid)}`);
  } catch {
    /* ignore */
  }
}

function currentRow(i: number) {
  return el('div', { class: 'set-row set-row--current' }, [
    el('span', { class: 'set-row__num' }, [`${i}`]),
    el('span', { class: 'set-row__cell dim' }, ['agora']),
    el('span', { class: 'set-row__cell dim' }, ['↓']),
    el('span', {}),
  ]);
}

function formatLastSet(last: LastSet[] | null): string {
  if (!last || last.length === 0) return '—';
  const top = last.reduce((a, b) => (b.weight_kg > a.weight_kg ? b : a));
  return `${top.weight_kg}kg × ${top.reps}`;
}

function formatTarget(s: { weight_kg: number; reps: number; reason: string }): string {
  if (s.reason === 'first-time') return 'Calibrar';
  return `${s.weight_kg}kg × ${s.reps}`;
}

type ExerciseProgress = {
  exercise: WorkoutExercise;
  logged: number;
  needed: number;
  done: boolean;
};

function computeProgress(state: SessionState): ExerciseProgress[] {
  return state.exercises.map((ex) => {
    const logged = state.logged.filter((s) => s.exercise_id === ex.id).length;
    return { exercise: ex, logged, needed: ex.default_sets, done: logged >= ex.default_sets };
  });
}

function renderFinishCard(state: SessionState) {
  activeTimer?.stop();

  const progress = computeProgress(state);
  const allDone = progress.every((p) => p.done);
  const totalSets = state.logged.length;
  const exsDone = progress.filter((p) => p.done).length;
  const firstPending = progress.find((p) => !p.done);

  const notes = el('textarea', {
    class: 'input',
    rows: 3,
    placeholder: 'Notas (opcional): como foi, dor, observações…',
    style:
      'min-height: 96px; padding-top: var(--space-3); padding-bottom: var(--space-3); resize: vertical',
  });

  const finish = el(
    'button',
    {
      class: 'btn btn--primary btn--cta',
      type: 'button',
      disabled: !allDone,
      onclick: async () => {
        if (!allDone) return;
        finish.disabled = true;
        try {
          await flush();
          await api.post(`/sessions/${state.sessionId}/finish`, { notes: notes.value || null });
          void wakeLock.release();
          toast('Treino finalizado!');
          navigate('/');
        } catch (err) {
          // Server might still reject (race condition with outbox flush).
          if (err instanceof ApiError && err.status === 409) {
            toast('Faltam séries pra finalizar');
          } else {
            toast('Erro ao finalizar. Tenta de novo.');
          }
          finish.disabled = false;
        }
      },
    },
    [allDone ? 'Finalizar treino' : `Faltam ${state.exercises.length - exsDone} exercício(s)`],
  );

  const checklist = el(
    'ul',
    { class: 'stack stack--sm', style: 'margin-bottom: var(--space-4)' },
    progress.map((p, idx) =>
      el(
        'li',
        {
          class: 'card row row--between',
          style: p.done ? '' : 'border-color: var(--warning)',
          onclick: () => {
            state.currentIdx = idx;
            paint(state);
          },
        },
        [
          el('div', {}, [
            el('div', { style: 'font-weight: 600' }, [p.exercise.name]),
            el('div', { class: 'dim', style: 'font-size: var(--text-sm)' }, [
              `${p.logged}/${p.needed} séries`,
            ]),
          ]),
          el(
            'span',
            { style: `font-size: var(--text-xl); color: var(--${p.done ? 'success' : 'warning'})` },
            [p.done ? '✓' : '◯'],
          ),
        ],
      ),
    ),
  );

  mount(
    el('main', { class: 'view' }, [
      el('header', { class: 'session-head' }, [el('div', { class: 'h2' }, ['Resumo da sessão'])]),
      el('section', { class: 'card', style: 'margin-bottom: var(--space-4)' }, [
        el('div', { class: 'row row--between' }, [
          el('div', {}, [
            el('div', { class: 'field__label' }, ['Sets logados']),
            el('div', { class: 'bignum' }, [String(totalSets)]),
          ]),
          el('div', {}, [
            el('div', { class: 'field__label' }, ['Exercícios']),
            el('div', { class: 'bignum' }, [`${exsDone}/${state.exercises.length}`]),
          ]),
        ]),
      ]),

      el('div', { class: 'field__label', style: 'margin-bottom: var(--space-2)' }, [
        allDone ? 'Tudo pronto.' : 'Pra finalizar, complete todos:',
      ]),
      checklist,

      el('div', { class: 'field', style: 'margin-bottom: var(--space-4)' }, [
        el('div', { class: 'field__label' }, ['Notas']),
        notes,
      ]),

      finish,
      el(
        'button',
        {
          class: 'btn btn--ghost btn--cta',
          type: 'button',
          style: 'margin-top: var(--space-2)',
          onclick: () => {
            // Jump back to first incomplete exercise, or last one.
            state.currentIdx = firstPending
              ? state.exercises.findIndex((e) => e.id === firstPending.exercise.id)
              : Math.max(0, state.exercises.length - 1);
            paint(state);
          },
        },
        [firstPending ? `Ir pra ${firstPending.exercise.name}` : 'Voltar pro treino'],
      ),
    ]),
  );
}
